import React, { useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { selectTotalUsedCargoTonnage } from "../../../../store/selectors/derivedSelectors";
import { setSupplyUnits } from "../../../../store/features/shipBuilderSlice";
import { formatCurrency } from "../../../../utils/formatting";

/**
 * Component for managing Supply Units (SU) according to Traveller rules
 * Military capital ships consume SU equal to tonnage/100 per day
 * 100 SU = 1 ton of supplies, minimum 100 days recommended
 */
export const SupplyUnitsManager: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipSize = useAppSelector((state) => state.shipBuilder.shipSize);
  const usedCargo = useAppSelector(selectTotalUsedCargoTonnage);
  const availableCargo = shipSize - usedCargo;
  // const weightOverrideActive = useAppSelector(selectors.selectOverrideWeight);

  const supplyUnits = useAppSelector(selectors.selectSupplyUnits);
  const supplyTons = useAppSelector(selectors.selectSupplyTons);
  const [daysOfSupply, setDaysOfSupply] = useState<number>(100);

  // Calculate supply requirements based on Traveller rules
  const dailyConsumption = Math.ceil(shipSize / 100); // SU per day
  const recommendedSupplyTons = Math.ceil((dailyConsumption * 100) / 100); // 100 days worth
  const maximumSupplyTons = Math.max(0, Math.floor(availableCargo)); // Ensure non-negative
  const supplyUnitsPerTon = 100; // 100 SU = 1 ton

  const handleSupplyUnitsChange = (units: number) => {
    const maxUnits = maximumSupplyTons * supplyUnitsPerTon;
    const clampedUnits = Math.max(0, Math.min(units, maxUnits));
    dispatch(setSupplyUnits(clampedUnits));
    setDaysOfSupply(
      clampedUnits > 0 ? Math.ceil(clampedUnits / dailyConsumption) : 0,
    );
  };

  const handleDaysOfSupplyChange = (days: number) => {
    const units = Math.ceil(days * dailyConsumption);
    const maxUnits = maximumSupplyTons * supplyUnitsPerTon;
    const clampedUnits = Math.min(units, maxUnits);
    dispatch(setSupplyUnits(clampedUnits));
    setDaysOfSupply(clampedUnits > 0 ? days : 0);
  };

  const handleSetRecommended = () => {
    const recommendedUnits = dailyConsumption * 100; // 100 days worth
    const maxUnits = maximumSupplyTons * supplyUnitsPerTon;
    const clampedUnits = Math.min(recommendedUnits, maxUnits);
    dispatch(setSupplyUnits(clampedUnits));
    setDaysOfSupply(clampedUnits > 0 ? 100 : 0);
  };

  const handleSetMaximum = () => {
    const maxUnits = maximumSupplyTons * supplyUnitsPerTon;
    dispatch(setSupplyUnits(maxUnits));
    setDaysOfSupply(
      maxUnits > 0 ? Math.ceil(maxUnits / dailyConsumption) : 0,
    );
  };

  const handleAssignSupplyUnits = () => {
    // This function can be used for additional validation or processing
    console.log("Supply units assigned:", supplyUnits);
  };

  return (
    <div className="space-y-6">
      {/* Supply Requirements */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-4">
          Supply Requirements
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-blue-800 dark:text-blue-300">Ship Size:</span>
            <span className="ml-2 text-blue-900 dark:text-blue-100">
              {formatCurrency(shipSize)} tons
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-800 dark:text-blue-300">Daily Consumption:</span>
            <span className="ml-2 text-blue-900 dark:text-blue-100">
              {dailyConsumption} SU/day
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-800 dark:text-blue-300">Recommended Supply:</span>
            <span className="ml-2 text-blue-900 dark:text-blue-100">
              {recommendedSupplyTons} tons
            </span>
          </div>
          <div>
            <span className="font-medium text-blue-800 dark:text-blue-300">Available Cargo:</span>
            <span className="ml-2 text-blue-900 dark:text-blue-100">
              {availableCargo.toFixed(1)} tons
            </span>
          </div>
        </div>
      </div>

      {/* Configure Supply Units */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
          Configure Supply Units
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Supply Units Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Supply Units (SU)
            </label>
            <input
              type="number"
              value={supplyUnits}
              onChange={(e) => handleSupplyUnitsChange(parseInt(e.target.value) || 0)}
              min={0}
              max={maximumSupplyTons * supplyUnitsPerTon}
              step={100}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum: {maximumSupplyTons * supplyUnitsPerTon} SU
            </p>
          </div>

          {/* Days of Supply Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Days of Supply
            </label>
            <input
              type="number"
              value={daysOfSupply}
              onChange={(e) => handleDaysOfSupplyChange(parseInt(e.target.value) || 0)}
              min={0}
              max={Math.ceil((maximumSupplyTons * supplyUnitsPerTon) / dailyConsumption)}
              step={1}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum: {Math.ceil((maximumSupplyTons * supplyUnitsPerTon) / dailyConsumption)} days
            </p>
          </div>
        </div>

        {/* Quick Set Buttons */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={handleSetRecommended}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
          >
            Set Recommended (100 days)
          </button>
          <button
            onClick={handleSetMaximum}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            Set Maximum
          </button>
        </div>

        {/* Current Configuration Summary */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-3">
            Current Configuration
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Supply Units:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">
                {supplyUnits} SU
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Supply Tons:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">
                {supplyTons?.toFixed(2) || "0.00"} tons
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700 dark:text-gray-300">Days of Supply:</span>
              <span className="ml-2 text-gray-900 dark:text-gray-100">
                {daysOfSupply} days
              </span>
            </div>
          </div>
        </div>

        {/* Assign Button */}
        <button
          onClick={handleAssignSupplyUnits}
          disabled={supplyUnits === 0}
          className={`w-full py-3 px-4 rounded-md font-medium transition-colors ${
            supplyUnits === 0
              ? "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
              : "bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
          }`}
        >
          Assign Supply Units
        </button>
      </div>
    </div>
  );
};
